import { FormEvent, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button, RowSpacer } from '../atoms'
import { useUserLanguage } from '@/customHooks/useUserLanguage'
import ReactPasswordChecklist from 'react-password-checklist'
import textsData from '@/textData/SignupFormTexts.json'
import {  CurrencyOption, MessageCodeOption, ResponseType, SignupNewUserMutation, UserLanguageOption, useSignupNewUserMutation } from '@/graphql/generated'
import { FetchResult } from '@apollo/client'
import { pathURL } from '@/components/constants/urls'
import { useErrorHandlerContext } from '@/customHooks'
import { Col, Row } from 'react-grid-system'
import {
  NormalFormEmailField,
  NormalFormPasswordField,
  NormalFormTextField,
} from '../molecules'
import { WarnText } from '../atoms/WarnText'

type UserDataProps = {
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  password: string,
  passwordConfirm: string,
}

const emptyUserData: UserDataProps = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  passwordConfirm: '',
}

const FormContainer = styled.form`
  margin: 0 auto;
  padding: 20px;
  width: 80%;
`;

const StyledCheckBox = styled.div`
  & > input {
    width: 24px;
    height: 24px;
  }
  & > label {
    margin-left: ${({ theme }) => theme.spacing.md};
  }
`

/* const WarnText = styled.p<{ $isHidden: boolean }>`
  color: ${({ theme }) => theme.colors.warn};
  font-size: 14px;
  font-weight: 600;
  visibility: ${({ $isHidden }) => $isHidden ? 'hidden' : 'visible'};
  margin-top: 0;
  margin-left: 1rem;
` */

export const SignupForm = () => {
  const userLanguage = useUserLanguage();
  const userLanguageOption = userLanguage === 'es' ? UserLanguageOption.Es : UserLanguageOption.En
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [isChecked, setIsChecked] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [isUserWarnHidden, setIsUserWarnHidden] = useState(true)

  const [password, setPassword] = useState<string | null>(null)
  const [passwordConfirmation, setPasswordConfirmation] = useState<string | null>(null)

  const signupDataRef = useRef(emptyUserData)

  const { open: errorHandle } = useErrorHandlerContext()

  const [signup, { loading }] = useSignupNewUserMutation()
  
  const navigate = useNavigate()

  const onSuccessHandle = (response: FetchResult<SignupNewUserMutation>) => {
    const signupResponse = response.data?.signup
    
    if(!signupResponse) return

    if (signupResponse.responseType === ResponseType.Success) {
      navigate(
        `/${pathURL.LOGIN}/${pathURL.OTP_VALIDATION}`,
        { state: {
          isForRegister: true,
          email: signupDataRef.current.email,
        }},
      )
    } else if (signupResponse.code === MessageCodeOption.UserExist) {
      setIsUserWarnHidden(false)
    }
  }

  const onChangeHandle = (key: keyof UserDataProps, value: string | null) => {
    if (!value) return

    signupDataRef.current[key] = value
  }
  const onSubmitHandle = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    signup({ variables: {
      firstName: signupDataRef.current.firstName,
      lastName: signupDataRef.current.lastName,
      email: signupDataRef.current.email,
      phoneNumber: signupDataRef.current.phone,
      password: signupDataRef.current.password,
      userLanguageOption,
      preferredCurrency: CurrencyOption.Usd,
    }})
    .then(onSuccessHandle)
    .catch(errorHandle)
  }
  /* 
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); 

    signup({ variables: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phone,
      password: userData.password,
      userLanguageOption,
      preferredCurrency: CurrencyOption.Usd,
    }})
    .then(onSuccessHandle)
    .catch(errorHandle)
  };
 */
  return (
    <FormContainer onSubmit={onSubmitHandle}>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.firstName}
            name="firstName"
            placeholder={texts.firstNamePlaceholder}
            required
            onChange={(value) => onChangeHandle('firstName', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.lastName}
            name="lastName"
            placeholder={texts.lastNamePlaceholder}
            required
            onChange={(value) => onChangeHandle('lastName', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormEmailField
            label={texts.email}
            name="email"
            placeholder={texts.emailPlaceholder}
            required
            onChange={(value) => onChangeHandle('email', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <WarnText
            hidden={isUserWarnHidden}
            text={texts.emailWarnText}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.phoneNumber}
            name="phone"
            placeholder={texts.phoneNumberPlaceholder}
            required
            onChange={(value) => onChangeHandle('phone', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormPasswordField
            label={texts.password}
            name="password"
            placeholder={texts.passwordPlaceholder}
            required
            onChange={setPassword}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormPasswordField
            label={texts.passwordConfirmation}
            name="password"
            placeholder={texts.passwordConfirmationPlaceholder}
            required
            onChange={setPasswordConfirmation}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ReactPasswordChecklist
            rules={["minLength","specialChar","number","capital","match"]}
				    minLength={5}
				    value={password ?? ''}
				    valueAgain={passwordConfirmation ?? ''}
				    onChange={(value) => {
              console.log(value)
              setIsPasswordValid(value)
              if (value) onChangeHandle('password', password)
              else  onChangeHandle('password', null)
            }}
          />
        </Col>
      </Row>
      <RowSpacer height={3} />
      <Row>
        <Col>
          <StyledCheckBox>
            <input type="checkbox" id="acceptTermscheck" checked={isChecked} onChange={() => setIsChecked((prev) => !prev)} />
            <label htmlFor="acceptTermscheck">
              Acepto los <Link to="">Terminos</Link> & <Link to="">Condiciones</Link>
            </label>
          </StyledCheckBox>
        </Col>
      </Row>
      <RowSpacer height={3} />
      <Row>
        <Col>
          <Button
            submit
            upperCased
            disabled={!(isChecked && isPasswordValid)}
            isLoading={loading}
          >
            {texts.button}
          </Button>
        </Col>
      </Row>
    </FormContainer>
  )

/* 
  return (
    <FormContainer onSubmit={handleSubmit}>
      <FormTextField
        name="firstName"
        placeholder={texts.firstNamePlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, firstName: value}))}
      >
        {texts.firstName}
      </FormTextField>
      <FormTextField
        name="lastName"
        placeholder={texts.lastNamePlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, lastName: value}))}
      >
        {texts.lastName}
      </FormTextField>
      <FormEmailField
        name="email"
        placeholder={texts.emailPlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, email: value}))}
      >
        {texts.email}
      </FormEmailField>
      <WarnText $isHidden={isUserWarnHidden}>{texts.emailWarnText}</WarnText>
      <FormPhoneField
        name="phone"
        placeholder={texts.phoneNumberPlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, phone: value}))}
      >
        {texts.phoneNumber}
      </FormPhoneField>
      <FormPasswordField
        name="password"
        placeholder={texts.passwordPlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, password: value }))}
      >
        {texts.password}
      </FormPasswordField>
      <FormPasswordField
        name="password"
        placeholder={texts.passwordConfirmationPlaceholder}
        action={(value) => setUserData((prev) => ({ ...prev, passwordConfirm: value }))}
      >
        {texts.passwordConfirmation}
      </FormPasswordField>
      <WarnText $isHidden={isWarnTextHidden}>{texts.passwordMismatchWarning}</WarnText>
      <StyledCheckBox>
        <input type="checkbox" id="acceptTermscheck" checked={isChecked} onChange={() => setIsChecked((prev) => !prev)} />
        <label htmlFor="acceptTermscheck">
          Acepto los <Link to="">Terminos</Link> & <Link to="">Condiciones</Link>
        </label>
      </StyledCheckBox>
      <br />
      <Button
        submit
        upperCased
        disabled={!isChecked}
        isLoading={loading}
      >
        {texts.button}
      </Button>
    </FormContainer>
  );
   */
};